'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useStore } from 'zustand';
import { getLabsStore, type LabsStore, type LabsStoreApi, type LabsStoreOptions } from './store';
import type { VerticalPack } from './types';

/**
 * The active vertical's runtime — its content pack and its flow store — supplied to every screen.
 *
 * WHY CONTEXT (and not props): the step registry is `Record<Step, ComponentType>` — screens take NO
 * props, and the orchestrator renders `SCREENS[step]` inside AnimatePresence. Threading content
 * through that registry would mean typing every screen's props and rewriting the orchestrator.
 * Instead the engine wraps the sprint in a `PackProvider` and each screen swaps its hardcoded
 * content import (`import { M1_CARDS } from '../_data/m1-cards'`) for `usePack()`, and its store
 * import for `useLabsStore()`. The registry, the orchestrator and the animation wiring are untouched.
 *
 * The STORE rides in the same context because it is per-vertical (see `./store.ts`): a screen must
 * never reach for a module-level store singleton, or Finance and Data & Analytics would share state.
 */

interface LabsRuntime {
  pack: VerticalPack;
  store: LabsStoreApi;
}
const PackContext = createContext<LabsRuntime | null>(null);
interface PackProviderProps {
  children: ReactNode;
  pack: VerticalPack;
  /**
   * Which FLOW over this pack. Omit for the gated sprint (all 18 steps, the sprint's draft key) —
   * that is every existing caller. The public preview passes its shortened sequence and its own
   * variant so the two never share a store or a persisted draft.
   */
  storeOptions?: LabsStoreOptions;
}

/** Wraps the sprint in the active vertical's content pack + store. Rendered once, by the route. */
export function PackProvider({
  children,
  pack,
  storeOptions
}: PackProviderProps): ReactNode {
  // `getLabsStore` is cached per slug (+ variant), so this is the same store instance across
  // re-renders and across remounts of the provider — the vertical's singleton.
  //
  // `storeOptions` is read on FIRST construction only, which is inherent to the cache and fine: a
  // surface passes one fixed sequence for its lifetime. It is deliberately not in the dep array —
  // callers pass an object literal, so depending on it would rebuild the runtime every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies(storeOptions): the store is cached by variant; re-reading a fresh literal each render would thrash the runtime for no behavioural gain.
  const runtime = useMemo<LabsRuntime>(() => ({
    pack,
    store: getLabsStore(pack, storeOptions)
  }), [pack]);
  return <PackContext.Provider value={runtime}>{children}</PackContext.Provider>;
}

/**
 * The active vertical's runtime. Throws outside a `PackProvider` — a screen with no pack has no
 * content, so failing loudly at the boundary beats rendering an empty sprint.
 */
export function useLabsRuntime() {
  const runtime = useContext(PackContext);
  if (runtime === null) {
    throw new Error("useLabsRuntime() must be called inside a <PackProvider>. Did the Labs route forget to wrap it?");
  }
  return runtime;
}

/** Read the active vertical's CONTENT (cards, questions, subjects, copy, recommendations config). */
export function usePack() {
  return useLabsRuntime().pack;
}

/**
 * Read the active vertical's FLOW STATE. Drop-in for finance's `useFinanceDiscoveryStore(selector)`
 * — same selector signature, but bound to the vertical in context rather than a module singleton.
 */
export function useLabsStore(selector) {
  return useStore(useLabsRuntime().store, selector);
}